import { useState, useMemo, useCallback } from 'react';
import type { BillableItem } from '@/lib/schemas';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { BillableItemInlineForm } from './BillableItemInlineForm';
import { BillableItemReceiptUpload } from '@/components/BillableItemReceiptUpload';
import { DueDateBadge } from '@/components/DueDateBadge';
import { Ban, ChevronDown, ChevronRight, GripVertical, Paperclip } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useReorderBillableItems } from '@/hooks';
import { useBillableItemFileCounts } from '@/hooks/useBillableItemFiles';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Get preview of description for table display
 * Shows first line only, truncated to maxLength
 */
function getDescriptionPreview(description: string, maxLength = 80): string {
  const firstLine = description.split('\n')[0];
  if (firstLine.length > maxLength) {
    return firstLine.substring(0, maxLength) + '...';
  }
  return firstLine;
}

// Helper functions moved outside component for reuse
const formatCurrency = (value: number | undefined) => {
  if (value === undefined || value === null) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
};

const formatStatus = (status: string | undefined) => {
  if (!status) return 'Not Started';
  const statusMap: Record<string, string> = {
    not_started: 'Not Started',
    in_progress: 'In Progress',
    complete: 'Complete',
    on_hold: 'On Hold',
  };
  return statusMap[status] || status;
};

const formatCategory = (category: string) => {
  const categoryMap: Record<string, string> = {
    professional_fee: 'Professional Fee',
    add_service: 'Add Service',
    billable_expense: 'Billable Expense',
    pass_through: 'Pass-Through',
    internal_work: 'Internal Work',
  };
  return categoryMap[category] || category;
};

const getStatusColor = (status: string | undefined) => {
  if (!status || status === 'not_started') return 'bg-slate-100 text-slate-600';
  if (status === 'in_progress') return 'bg-sky-50 text-sky-700';
  if (status === 'complete') return 'bg-emerald-50 text-emerald-700';
  if (status === 'on_hold') return 'bg-amber-50 text-amber-700';
  return 'bg-slate-100 text-slate-600';
};

const getCategoryColor = (category: string) => {
  if (category === 'professional_fee') return 'bg-violet-50 text-violet-700';
  if (category === 'add_service') return 'bg-sky-50 text-sky-700';
  if (category === 'billable_expense') return 'bg-amber-50 text-amber-700';
  if (category === 'pass_through') return 'bg-teal-50 text-teal-700';
  if (category === 'internal_work') return 'bg-slate-100 text-slate-600';
  return 'bg-slate-100 text-slate-600';
};

// 9.9d CONTRACT-02: Sortable row component for drag-drop reordering
interface SortableRowProps {
  item: BillableItem;
  isExpanded: boolean;
  onToggleExpand: () => void;
  receiptCount?: number;
}

function SortableRow({ item, isExpanded, onToggleExpand, receiptCount }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={`hover:bg-gray-50 ${isDragging ? 'bg-blue-50' : ''}`}
      data-testid={`billable-item-row-${item.id}`}
    >
      {/* Drag handle */}
      <TableCell
        className="w-[40px] cursor-grab active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4 text-gray-400 hover:text-gray-600" />
      </TableCell>
      {/* Expand/collapse */}
      <TableCell
        className="w-[40px] cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpand();
        }}
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </TableCell>
      <TableCell
        className="max-w-md cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpand();
        }}
      >
        <div className="flex items-center gap-2" title={item.description}>
          <p className={`truncate ${item.cancelled ? 'line-through text-muted-foreground' : ''}`}>
            {getDescriptionPreview(item.description)}
          </p>
          {/* Phase 10b.2: Receipt indicator with count badge for billable_expense items */}
          {item.category === 'billable_expense' && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-xs text-amber-600"
              title={receiptCount ? `${receiptCount} receipt${receiptCount !== 1 ? 's' : ''} attached` : 'Receipts can be attached to this expense item'}
              data-testid={`receipt-indicator-${item.id}`}
            >
              <Paperclip className="h-3 w-3" />
              {receiptCount != null && receiptCount > 0 && (
                <span className="text-xs font-medium" data-testid={`receipt-count-${item.id}`}>{receiptCount}</span>
              )}
            </span>
          )}
          {/* 10c.2: Recurring amount badge — shows interval for proposal partnership fees */}
          {item.amount_recurring != null && item.amount_recurring > 0 && item.recurring_interval && (
            <span
              className="inline-flex items-center rounded-full bg-indigo-50 px-1.5 py-0.5 text-xs text-indigo-700 font-medium"
              title={`Recurring ${item.recurring_interval} fee: ${formatCurrency(item.amount_recurring)}/${item.recurring_interval === 'monthly' ? 'mo' : item.recurring_interval === 'quarterly' ? 'qtr' : 'yr'}`}
            >
              {item.recurring_interval === 'monthly' ? 'Monthly' : item.recurring_interval === 'quarterly' ? 'Quarterly' : 'Annual'}
            </span>
          )}
          {item.item_status && item.item_status !== 'confirmed' && (
            <Badge
              variant={
                item.item_status === 'proposed' ? 'secondary' :
                item.item_status === 'proposed-optional' ? 'outline' :
                'destructive'
              }
            >
              {item.item_status === 'proposed' ? 'Proposed' :
               item.item_status === 'proposed-optional' ? 'Optional' :
               'Declined'}
            </Badge>
          )}
          {/* Cancelled badge (P4 spec follow-up). Mutually exclusive with the
              Invoiced badge — a cancelled item has been removed from all
              draft/ready invoices and can't be cancelled while on a sent
              invoice, so the cancelled state takes precedence. Icon + text
              so accessibility doesn't rely on colour alone. */}
          {item.cancelled ? (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600 font-medium"
              title="This billable item has been cancelled"
              data-testid={`cancelled-badge-${item.id}`}
            >
              <Ban className="h-3 w-3" />
              Cancelled
            </span>
          ) : (
            (item.invoiced_amount || 0) > 0 && (
              <span
                className="inline-flex items-center rounded-full bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700 font-medium"
                title={`${formatCurrency(item.invoiced_amount)} invoiced — editing will not update the invoice`}
              >
                Invoiced
              </span>
            )
          )}
        </div>
      </TableCell>
      <TableCell>
        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getStatusColor(item.status)}`}>
          {formatStatus(item.status)}
        </span>
      </TableCell>
      <TableCell>
        <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getCategoryColor(item.category)}`}>
          {formatCategory(item.category)}
        </span>
      </TableCell>
      <TableCell>
        {item.due_date ? (
          <DueDateBadge dueDate={item.due_date} />
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className={`text-right font-mono ${item.cancelled ? 'line-through text-muted-foreground' : ''}`}>
        {formatCurrency(item.amount)}
      </TableCell>
      <TableCell className="text-right font-mono">
        {formatCurrency(item.invoiced_amount || 0)}
      </TableCell>
      <TableCell className="text-right font-mono">
        {formatCurrency(item.paid_amount || 0)}
      </TableCell>
    </TableRow>
  );
}

interface BillableItemsTableProps {
  items: BillableItem[];
  projectId: string;
  projectNumber: string | null;  // Phase 9.6a: Changed from number to string, null for proposals/templates
  workflowStage?: string; // Batch 7.3.4: Determines default item_status for new items
  onUpdate: (item: BillableItem) => void;
  onDelete: (itemId: string) => void;
  isCreatingNew?: boolean;
  onCancelCreate?: () => void;
  onCreateSuccess?: () => void;
}

export function BillableItemsTable({
  items,
  projectId,
  projectNumber,
  workflowStage,
  onUpdate,
  onDelete,
  isCreatingNew = false,
  onCancelCreate,
  onCreateSuccess,
}: BillableItemsTableProps) {
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  // 9.9d CONTRACT-02: Local state for optimistic reorder UI
  const [localItems, setLocalItems] = useState<BillableItem[]>(items);
  const reorderMutation = useReorderBillableItems();

  // Receipt count fetching for billable_expense items
  const queryClient = useQueryClient();
  const expenseItemIds = useMemo(
    () => items
      .filter(item => item.category === 'billable_expense')
      .map(item => item.id),
    [items]
  );
  const { data: receiptCounts } = useBillableItemFileCounts(expenseItemIds);

  // Callback to refresh receipt counts when files are uploaded/deleted
  const handleReceiptFilesChange = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['billable-item-file-counts'] });
  }, [queryClient]);

  // Sync local items with props when items change (e.g., after server update)
  useMemo(() => {
    setLocalItems(items);
  }, [items]);

  const toggleExpand = (itemId: string) => {
    setExpandedItemId(expandedItemId === itemId ? null : itemId);
  };

  // 9.9d CONTRACT-02: dnd-kit sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum drag distance before activation
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 9.9d CONTRACT-02: Handle drag end - reorder items and persist
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localItems.findIndex((item) => item.id === active.id);
      const newIndex = localItems.findIndex((item) => item.id === over.id);

      // Optimistic UI update
      const newItems = arrayMove(localItems, oldIndex, newIndex);
      setLocalItems(newItems);

      // Persist new order to backend
      const reorderPayload = newItems.map((item, index) => ({
        id: item.id,
        display_order: index,
      }));
      reorderMutation.mutate(reorderPayload);
    }
  };

  // Get item IDs for SortableContext
  const itemIds = useMemo(() => localItems.map((item) => item.id), [localItems]);

  return (
    // MED-06: Added overflow-x-auto for responsive horizontal scrolling on narrow screens
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {/* 9.9d CONTRACT-02: Added drag handle column */}
            <TableHead className="w-[40px]"></TableHead>
            <TableHead className="w-[40px]"></TableHead>
            <TableHead>Description</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Due Date</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-right">Invoiced</TableHead>
            <TableHead className="text-right">Paid</TableHead>
          </TableRow>
        </TableHeader>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            <TableBody>
              {/* Inline Creation Row */}
              {isCreatingNew && (
                <TableRow key="create-row" data-testid="billable-item-create-row">
                  <TableCell colSpan={9} className="bg-blue-50 p-4">
                    <BillableItemInlineForm
                      mode="create"
                      projectId={projectId}
                      projectNumber={projectNumber}
                      workflowStage={workflowStage}
                      onSave={() => {
                        if (onCreateSuccess) onCreateSuccess();
                      }}
                      onCancel={() => {
                        if (onCancelCreate) onCancelCreate();
                      }}
                    />
                  </TableCell>
                </TableRow>
              )}

              {localItems.map((item) => (
                <SortableRow
                  key={item.id}
                  item={item}
                  isExpanded={expandedItemId === item.id}
                  onToggleExpand={() => toggleExpand(item.id)}
                  receiptCount={receiptCounts?.[item.id]}
                />
              ))}

              {/* Expanded Edit Form - rendered outside sortable context */}
              {expandedItemId && (() => {
                const expandedItem = localItems.find((i) => i.id === expandedItemId);
                return (
                  <TableRow key={`${expandedItemId}-form`} data-testid="billable-item-inline-form">
                    <TableCell colSpan={9} className="bg-gray-50 p-4">
                      <BillableItemInlineForm
                        item={expandedItem}
                        projectId={projectId}
                        projectNumber={projectNumber}
                        workflowStage={workflowStage}
                        onSave={() => {
                          if (expandedItem) onUpdate(expandedItem);
                          setExpandedItemId(null);
                        }}
                        onCancel={() => setExpandedItemId(null)}
                        onDelete={() => {
                          if (expandedItemId) onDelete(expandedItemId);
                          setExpandedItemId(null);
                        }}
                      />
                      {/* Phase 10b.2: Receipt uploads for billable_expense items in inline view */}
                      {expandedItem?.category === 'billable_expense' && (
                        <BillableItemReceiptUpload
                          billableItemId={expandedItemId}
                          onFilesChange={handleReceiptFilesChange}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })()}
            </TableBody>
          </SortableContext>
        </DndContext>
        <TableFooter>
          <TableRow>
            {/* 9.9d CONTRACT-02: Updated colspan for new column */}
            <TableCell colSpan={6} className="text-right font-semibold">
              Total:
            </TableCell>
            <TableCell className="text-right font-mono font-semibold">
              {formatCurrency(localItems.reduce((sum, item) => sum + (item.amount || 0), 0))}
            </TableCell>
            <TableCell className="text-right font-mono font-semibold">
              {formatCurrency(localItems.reduce((sum, item) => sum + (item.invoiced_amount || 0), 0))}
            </TableCell>
            <TableCell className="text-right font-mono font-semibold">
              {formatCurrency(localItems.reduce((sum, item) => sum + (item.paid_amount || 0), 0))}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
}
